import Divider from '@/components/ui/Divider';
import Shimmer from '@/components/ui/Shimmer';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { memo, useMemo } from 'react';
import classnames from 'classnames';

interface PageShimmerProps {
  show?: boolean;
}

const PageLoading: React.FC<PageShimmerProps> = memo(({ show }) => {
  const IsBelowLarge = useMediaQuery(ScreenSizeQueries.belowLg);
  const IsBelowMedium = useMediaQuery(ScreenSizeQueries.belowMd);

  const navMenuShimmer = useMemo(() => {
    const largeNav = (
      <div className={'container relative flex flex-col gap-5 pb-6 pt-7 md:py-5'}>
        <div className="flex items-center md:min-h-14 lg:items-stretch">
          {/* NAV left side */}
          <div className="flex w-full items-center justify-between lg:mr-auto lg:w-auto xl:gap-[43px]">
            <img className="h-6 md:h-8" src="/logo-red.svg" width="64" height="32" alt="PLI Logo" />
            <div className="ml-4 flex size-full items-center justify-between xl:ml-0 xl:gap-8">
              {Array.from({ length: 4 }, () => {
                return (
                  <div className="mr-2 flex h-14 w-24 items-center xl:mr-0">
                    <Shimmer className="h-6 w-full rounded-md" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* NAV right side */}
          <div className="ml-auto flex items-center gap-4 lg:gap-6">
            <Shimmer className="h-14 w-[370px] rounded-full" />
            <Shimmer className="h-5 w-[86px] rounded-md" />
            <Shimmer className="size-10 rounded-full" />
          </div>
        </div>
      </div>
    );

    const mediumNav = (
      <div className={'container relative flex flex-col gap-5 pb-6 pt-7 md:py-5'}>
        <div className="flex items-center md:min-h-14 lg:items-stretch">
          {/* NAV left side */}
          <div className="flex items-center justify-start">
            <Shimmer className="mr-5 size-10 rounded-md" />
            <img className="h-6" src="/logo-red.svg" width="64" height="32" alt="PLI Logo" />
          </div>

          <div className="ml-auto flex items-center gap-4 lg:gap-6">
            <Shimmer
              className="pointer-events-none hidden h-14 w-[450px] rounded-full
                md:absolute md:left-1/2 md:top-auto md:flex md:-translate-x-1/2
                lg:relative lg:left-auto lg:block lg:w-[370px] lg:translate-x-0"
            />
            <Shimmer className="size-10 rounded-full" />
          </div>
        </div>
      </div>
    );

    const smallNav = (
      <div className={'container relative flex flex-col gap-5 pb-6 pt-7 md:py-5'}>
        <div className="flex items-center md:min-h-14 lg:items-stretch">
          {/* NAV left side */}
          <div className="mr-4 flex items-center justify-start">
            <Shimmer className="mr-5 size-10 flex-auto rounded-md" />
            <img className="h-6" src="/logo-red.svg" width="64" height="32" alt="PLI Logo" />
          </div>

          {/* NAV right side */}
          <div className="ml-auto flex items-center gap-4 lg:gap-6">
            <Shimmer className="size-10 flex-auto rounded-full" />
          </div>
        </div>

        <Shimmer className="h-14 w-full flex-auto rounded-full" />
      </div>
    );

    return (
      <header className="sticky top-0 z-sticky-header bg-white transition-all">
        {IsBelowLarge ? (IsBelowMedium ? smallNav : mediumNav) : largeNav}
      </header>
    );
  }, [IsBelowLarge, IsBelowMedium]);

  if (!show) return null;

  return (
    <div className="h-screen">
      {navMenuShimmer}

      <Divider orientation="horizontal" color="light" />

      <div className="container relative mt-[-160px] bg-transparent pt-[192px] lg:mt-[-96px] lg:pt-[128px]">
        {/* Intro block */}
        <div className="mt-12 h-[336px] w-full sm:max-w-[66%] md:my-12">
          <Shimmer className="text-3-fixed-l h-20 w-full rounded-lg" />
          <Shimmer className="mt-4 h-48 w-full rounded-lg md:h-64 md:w-3/4" />
        </div>

        {/* Carousel */}
        <Shimmer className="h-[700px] w-full rounded-lg md:mt-16 lg:h-[428px]" />
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shimmer className="size-9 flex-auto rounded-full" />
            <Shimmer className="h-5 w-36 rounded-md" />
          </div>
        </div>

        {/* Trending */}
        <div className="module-spacing relative my-16 flex flex-col gap-8 overflow-x-clip md:overflow-x-hidden">
          <Shimmer className="h-20 w-5/6 rounded-lg lg:w-1/2 " />
          <div className="flex items-center justify-start gap-2">
            {Array.from({ length: 3 }, () => {
              return (
                <div className="flex h-14 w-48 items-center">
                  <Shimmer className="h-16 w-full rounded-md" />
                </div>
              );
            })}
          </div>
          <div className="flex gap-6 md:overflow-visible">
            {Array.from({ length: 5 }).map((_, index) => (
              <Shimmer
                key={index}
                className={classnames(
                  'size-full h-[400px] rounded-lg',
                  'min-w-[75%]',
                  'sm:min-w-[37.5%]',
                  'md:min-w-[calc(25%-18px)]'
                )}
              />
            ))}
          </div>
        </div>

        {/* Practice Areas */}
        <div className="my-16 flex flex-col gap-8">
          <Shimmer className="h-14 w-full rounded-full" />
          <Shimmer className="h-14 w-full rounded-full" />
          <Shimmer className="h-14 w-full rounded-full" />
          <Shimmer className="size-9 flex-auto rounded-full" />
        </div>

        <div className="container relative bg-white py-10 sm:py-16">
          <Shimmer className="h-[500px] w-full rounded-lg" />
          <Shimmer className="mt-8 h-60 w-full rounded-lg" />
        </div>

        {Array.from({ length: 6 }, () => {
          return (
            <div className="container relative border-b border-solid border-gray-light bg-white px-0 py-10 sm:py-16">
              <div className="mt-6 flex flex-col items-center justify-center gap-4 px-10 py-6 sm:mt-8 sm:gap-12 md:flex-row md:gap-10 lg:gap-[133px] lg:px-20 lg:py-16">
                <div>
                  <Shimmer className="h-80 w-96 self-start rounded-lg md:size-80" />
                </div>
                <div className="flex w-full flex-col gap-4 md:w-3/4">
                  <Shimmer className="my-6 h-24 w-full rounded-lg" />
                  <Shimmer className="mt-0 h-24 w-full rounded-lg md:w-3/4" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default PageLoading;
